/**
 * TypeScript view of the `lights_out` format.
 * Source of truth: `shared/schema/lights_out.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface LightsOutLimits {
  maxPresses?: number | null;
}

export interface LightsOutDefinition {
  puzzleType: "lights_out";
  typeVersion: number;
  /** Side of the square board, 3 to 7. */
  size: number;
  /** `size` rows of `size` characters: "1" lit, "0" dark. */
  lights: string[];
  limits?: LightsOutLimits;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** One press, as `[row, col]`. */
export type Press = [row: number, col: number];

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface LightsOutProgress {
  version: 1;
  /** The presses so far, in order. Replaying them over the pattern gives the position. */
  presses: Press[];
  /** The tail taken back, still redoable, most recent undo first. Never sent to the server. */
  undone?: Press[];
}

export interface LightsOutSubmission {
  presses: Press[];
}

/** The author's reference press sequence, in the submission format. */
export type LightsOutSolution = LightsOutSubmission;

export const MIN_SIZE = 3;
export const MAX_SIZE = 7;
export const MAX_PRESSES = 500;

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
